<template>
  <modal
    :show.sync="showAndClose"
    bodyClasses="pt-0"
    size="lg"
    @close="closeModal"
  >
    <template slot="header" class="pb-0">
      <h6 class="subtitle" id="modal-title-default">
        Tags
      </h6>
    </template>
    <div class="row forms mt-4">
      <div class="col-xl-6 col-md-12 col-sm-12 col-12 box-flex">
        <div class="card">
          <div class="row">
            <div class="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
              <base-input
                v-model.trim="$v.tag.name.$model"
                inputClasses="text-capitalize"
                label="Name"
                @input="changeName"
                placeholder="Name"
                id="name"
                name="name"
                tab-index="15"
                :error="
                  !$v.tag.name.minLength || $v.tag.name.$error
                    ? `Minimum ${$v.tag.name.$params.minLength.min} letters.`
                    : ''
                "
              />
            </div>

            <div class="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 mb-3">
              <label class="title-label">Description</label>
              <el-input
                v-model.trim="$v.tag.description.$model"
                placeholder="Description"
                id="Description"
                name="Description"
                maxlength="100"
                type="textarea"
                :autosize="{ minRows: 2, maxRows: 5 }"
                show-word-limit
              >
              </el-input>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 box-flex">
        <div class="card">
          <div class="table-tags">
            <b-table
              hover
              :items="tags"
              :fields="fields"
              :busy="loading"
              sort-icon-left
              responsive
              show-empty
              empty-filtered-text="There are no records matching your request"
              empty-text="There are no records to show"
            >
              <template #cell(actions)="data">
                <el-tooltip content="Delete" placement="bottom">
                  <span
                    class="icon-action text-danger ml-2"
                    @click="deleteTagById(data.item._id)"
                  >
                    <i class="flaticon-delete"></i>
                  </span>
                </el-tooltip>
              </template>
            </b-table>
          </div>
        </div>
      </div>

      <div class="modal-footer col-12">
        <base-button
          type="outline-neutral mb-0"
          class="min-button"
          @click="closeModal()"
        >
          <i class="flaticon-cancel"></i> Close
        </base-button>

        <base-button
          type="primary"
          class="min-button ml-auto mb-0"
          @click="saveTag()"
        >
          <i class="flaticon-floppy-disk"></i> Add
        </base-button>
      </div>
    </div>
  </modal>
</template>

<script>
import lodash from "lodash";
import { mapGetters } from "vuex";
import { TagsAPI } from "@/core/api";
import { TAG_TYPES } from "@/core/constants";
import {
  required,
  minLength,
  maxLength,
  helpers
} from "vuelidate/lib/validators";
const ValName = helpers.regex("alphaCha", /^[a-zA-ZÑñáéíóúüüÜÁÉÍÓÚ'-\s]*$/);

export default {
  name: "tags-manager",
  props: {
    show: {
      type: Boolean,
      default: false,
      description: "(closeable)"
    },
    type: {
      type: String,
      required: true,
      validator: value => Object.values(TAG_TYPES).includes(value),
      description: "Tag type managed by the modal (see TAG_TYPES)"
    }
  },
  data() {
    return {
      tag: {
        name: "",
        slug: "",
        description: ""
      },
      tags: [],
      loading: false,
      showAndClose: false,
      fields: [
        {
          key: "name",
          label: "Tags",
          sortable: false,
          class: "text-left",
          thStyle: { minWidth: "120px" }
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          class: "alinmov6",
          thStyle: { minWidth: "80px" }
        }
      ]
    };
  },
  watch: {
    show: {
      handler: function(current) {
        this.showAndClose = current;
        if (current) {
          this.getTags();
        }
      },
      immediate: true
    }
  },
  methods: {
    /**
     * Load the tags of the managed type from the requester catalog.
     */
    async getTags() {
      this.loading = true;
      try {
        let { data } = await TagsAPI.getTagsOptionsByType(
          this.type,
          this.sesion.token
        );
        this.tags = data || [];
      } catch (error) {
        this.tags = [];
      }
      this.loading = false;
    },
    /**
     * Validate the form and create the tag with the managed type.
     */
    async saveTag() {
      if (!this.$v.tag.$invalid) {
        let tag = { ...lodash.cloneDeep(this.tag), type: this.type };
        let saved = await this.createTag(tag);
        if (saved) {
          this.clearData();
          await this.getTags();
          this.$emit("changed");
        }
      } else {
        this.$v.tag.$touch();
        this.$notify({
          type: "warning",
          title: "Important!",
          message: "Please complete the required attributes",
          icon: "flaticon-warning-1"
        });
      }
    },
    async createTag(tag) {
      try {
        let { data } = await TagsAPI.createTags(tag, this.sesion.token);
        this.$notify({
          type: "success",
          title: "Success!",
          message: "The Tag has been Saved",
          icon: "flaticon-check-mark"
        });
        return data;
      } catch (error) {
        this.$notify({
          type: "danger",
          title: "Error!",
          message: this.getErrorMessage(
            error,
            "There was an error saving the Tag, please try again"
          ),
          icon: "flaticon-remove"
        });
        return null;
      }
    },
    async deleteTagById(tagId) {
      try {
        await TagsAPI.deleteTagById(tagId, this.sesion.token);
        this.$notify({
          type: "success",
          title: "Success!",
          message: "The Tag has been deleted",
          icon: "flaticon-check-mark"
        });
        await this.getTags();
        this.$emit("changed");
      } catch (error) {
        this.$notify({
          type: "danger",
          title: "Error!",
          message: this.getErrorMessage(
            error,
            "The Operation couldn't be completed. Please try again"
          ),
          icon: "flaticon-remove"
        });
      }
    },
    /**
     * Get the message returned by the API, or a default message when there is none.
     * @param {Object} error - The request error.
     * @param {string} defaultMessage - The message used when the API does not return one.
     * @returns {string} The error message to show.
     */
    getErrorMessage(error, defaultMessage) {
      let { response } = error;
      return (
        (response && response.data && response.data.message) || defaultMessage
      );
    },
    clearData() {
      this.$v.tag.$reset();
      this.tag.name = "";
      this.tag.slug = "";
      this.tag.description = "";
    },
    closeModal() {
      this.clearData();
      this.$emit("closeTagsModal", false);
    },
    changeName(name) {
      this.tag.slug = this.createSlug(name);
    },
    createSlug(name = "") {
      return name
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
    }
  },
  computed: {
    ...mapGetters(["sesion"])
  },
  validations: {
    tag: {
      name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(50),
        ValName
      },
      slug: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(50)
      },
      description: {
        minLength: minLength(0),
        maxLength: maxLength(100)
      }
    }
  }
};
</script>
